import aiLogger from './ai-logger';
import { Component } from "./component";

interface QuestionOption {
    label: string;
    value: string;
    recommended?: boolean;
    allowCustom?: boolean;
}

interface Question {
    id: string;
    question: string;
    options?: QuestionOption[];
    allowCustom?: boolean;
    customPlaceholder?: string;
}

interface QuestionPayload {
    requestId: string;
    questions: Question[];
    context?: string;
}

interface Answer {
    questionId: string;
    answer: string;
    isCustom: boolean;
}

export class AskQuestionsModal extends Component {
    private overlay!: HTMLDivElement;
    private modal!: HTMLDivElement;
    private contextEl!: HTMLDivElement;
    private bodyEl!: HTMLDivElement;
    private confirmBtn!: HTMLButtonElement;

    private currentPayload: QuestionPayload | null = null;
    private answers: Map<string, string> = new Map();
    private customInputs: Map<string, string> = new Map();

    /**
     * Container onde o overlay será inserido.
     * null = usa .chat-box-container (compat chat) ou document.body como fallback.
     */
    private hostContainer: HTMLElement | null = null;

    /**
     * Modo de operação: 'chat' aplica blur/block nas sidebars;
     * 'standalone' apenas exibe o overlay sem afetar sidebars.
     */
    private mode: 'chat' | 'standalone' = 'chat';

    setup() {
        this.createModal();
        this.preventClosing();
    }

    /**
     * Configura o modal para operar em modo standalone (fora do chat).
     * Deve ser chamado ANTES de showQuestions().
     * @param container Elemento onde o overlay será inserido (ex: popup body do ai-review-modal).
     */
    public setStandaloneMode(container: HTMLElement): void {
        this.mode = 'standalone';
        this.hostContainer = container;

        // Move overlay para o novo container se já criado
        if (this.overlay && this.overlay.parentElement !== container) {
            this.overlay.remove();
            container.appendChild(this.overlay);
        }
    }

    /**
     * Cria estrutura HTML do modal.
     * O overlay fica dentro do .chat-box-container (position: absolute),
     * desfocando apenas o conteúdo do chat. As sidebars ficam com pointer-events: none
     * enquanto o modal está aberto, impedindo cliques sem bloquear o modal.
     */
    private createModal() {
        this.overlay = document.createElement('div');
        this.overlay.className = 'ask-questions-overlay hidden';

        this.modal = document.createElement('div');
        this.modal.className = 'ask-questions-modal';

        const header = document.createElement('div');
        header.className = 'ask-questions-header';
        header.innerHTML = '&#x1F914; Athena precisa de informações';

        this.contextEl = document.createElement('div');
        this.contextEl.className = 'ask-questions-context hidden';

        this.bodyEl = document.createElement('div');
        this.bodyEl.className = 'ask-questions-body';

        const footer = document.createElement('div');
        footer.className = 'ask-questions-footer';

        this.confirmBtn = document.createElement('button');
        this.confirmBtn.className = 'ask-questions-confirm-btn';
        this.confirmBtn.textContent = 'Confirmar Respostas';
        this.confirmBtn.disabled = true;
        this.confirmBtn.addEventListener('click', () => this.confirmAnswers());

        footer.appendChild(this.confirmBtn);
        this.modal.appendChild(header);
        this.modal.appendChild(this.contextEl);
        this.modal.appendChild(this.bodyEl);
        this.modal.appendChild(footer);
        this.overlay.appendChild(this.modal);

        // Insere overlay dentro do container configurado
        const targetContainer = this.hostContainer
            || document.querySelector('.chat-box-container')
            || document.body;
        targetContainer.appendChild(this.overlay);

        this.injectStyles();
    }

    /**
     * Injeta estilos CSS seguindo o padrÃ£o visual dos asides do chat
     * (glassmorphism, var(--color-primary), border-radius: 30px)
     */
    private injectStyles() {
        if (document.getElementById('ask-questions-styles')) return;
        const style = document.createElement('style');
        style.id = 'ask-questions-styles';
        style.textContent = `
            /* Sidebars bloqueadas enquanto modal está ativo */
            .chat-sidebar.is-asking-blocked,
            .chat-files-sidebar.is-asking-blocked {
                pointer-events: none !important;
                user-select: none !important;
            }

            /* Overlay dentro do .chat-box-container */
            .ask-questions-overlay {
                position: absolute;
                inset: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                z-index: 1000;
                border-radius: 30px;
                overflow: hidden;
            }

            .ask-questions-overlay.hidden {
                display: none !important;
            }

            /* Blur no conteúdo do chat quando o modal está ativo */
            .chat-box-container.is-asking > *:not(.ask-questions-overlay) {
                filter: blur(4px);
                pointer-events: none;
                transition: filter 0.2s ease;
            }

            .ask-questions-modal {
                background: var(--chat-modal-bg, var(--chat-container-bg, rgba(255, 255, 255, 0.82)));
                backdrop-filter: blur(30px);
                -webkit-backdrop-filter: blur(30px);
                border: 1px solid var(--chat-modal-border, rgba(255, 255, 255, 0.3));
                box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
                border-radius: 22px;
                max-width: 480px;
                width: calc(100% - 48px);
                max-height: 78vh;
                display: flex;
                flex-direction: column;
                overflow: hidden;
                position: relative;
                z-index: 1001;
                color: var(--chat-modal-text, var(--chat-text-color, inherit));
            }

            @media (prefers-color-scheme: dark) {
                .ask-questions-modal {
                    background: var(--chat-modal-bg, var(--chat-container-bg, rgba(0, 0, 0, 0.24)));
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.37);
                    border-color: var(--chat-modal-border, rgba(53, 53, 53, 0.2));
                }
            }

            .ask-questions-header {
                padding: 16px 24px 14px;
                background: var(--chat-modal-header-bg, rgba(0, 0, 0, 0.10));
                backdrop-filter: blur(12px);
                -webkit-backdrop-filter: blur(12px);
                border-bottom: 1px solid rgba(0, 0, 0, 0.07);
                color: inherit;
                font-size: 15px;
                font-weight: 700;
                letter-spacing: -0.2px;
                border-radius: 30px 30px 0 0;
                display: flex;
                align-items: center;
                gap: 8px;
            }

            @media (prefers-color-scheme: dark) {
                .ask-questions-header {
                    background: var(--chat-modal-header-bg, rgba(255, 255, 255, 0.05));
                    border-bottom-color: rgba(255, 255, 255, 0.06);
                }
            }

            .ask-questions-context {
                padding: 12px 24px;
                background: rgba(0, 0, 0, 0.04);
                border-bottom: 1px solid rgba(0, 0, 0, 0.07);
                color: #555;
                font-size: 13px;
                line-height: 1.5;
            }

            .ask-questions-context.hidden {
                display: none !important;
            }

            @media (prefers-color-scheme: dark) {
                .ask-questions-context {
                    background: rgba(255,255,255,0.04);
                    border-bottom-color: rgba(255,255,255,0.07);
                    color: #aaa;
                }
            }

            .ask-questions-body {
                padding: 24px;
                overflow-y: auto;
                flex: 1;
            }

            .ask-questions-footer {
                padding: 16px 24px 20px;
                border-top: 1px solid rgba(0, 0, 0, 0.07);
                display: flex;
                justify-content: center;
                background: rgba(0, 0, 0, 0.02);
                border-radius: 0 0 22px 22px;
            }

            @media (prefers-color-scheme: dark) {
                .ask-questions-footer {
                    border-top-color: rgba(255,255,255,0.07);
                    background: rgba(255,255,255,0.02);
                }
            }

            .ask-questions-confirm-btn {
                padding: 12px 36px;
                background: var(--chat-btn-color, var(--color-primary));
                color: #fff;
                border: none;
                border-radius: 12px;
                font-size: 15px;
                font-weight: 600;
                cursor: pointer;
                transition: opacity 0.2s, transform 0.2s, box-shadow 0.2s;
                box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
            }

            .ask-questions-confirm-btn:disabled {
                opacity: 0.4;
                cursor: not-allowed;
                transform: none !important;
                box-shadow: none !important;
            }

            .ask-questions-confirm-btn:not(:disabled):hover {
                opacity: 0.88;
                transform: translateY(-1px);
                box-shadow: 0 6px 18px rgba(0, 0, 0, 0.22) !important;
            }

            .question-block {
                margin-bottom: 28px;
            }

            .question-number {
                font-size: 11px;
                font-weight: 700;
                color: var(--color-primary);
                text-transform: uppercase;
                letter-spacing: 0.8px;
                margin-bottom: 6px;
                opacity: 0.8;
            }

            .question-text {
                font-size: 15px;
                font-weight: 600;
                color: #1a1a1a;
                margin-bottom: 14px;
                line-height: 1.4;
            }

            @media (prefers-color-scheme: dark) {
                .question-text { color: #eee; }
            }

            .options-container {
                display: flex;
                flex-direction: column;
                gap: 8px;
            }

            .option-button {
                width: 100%;
                padding: 12px 16px;
                background: var(--chat-card-bg, rgba(255, 255, 255, 0.6));
                border: 1.5px solid rgba(0, 0, 0, 0.1);
                border-radius: 10px;
                cursor: pointer;
                display: flex;
                align-items: center;
                gap: 10px;
                font-size: 14px;
                color: var(--chat-text-color, #333);
                font-weight: 500;
                transition: all 0.15s ease;
                text-align: left;
            }

            @media (prefers-color-scheme: dark) {
                .option-button {
                    background: var(--chat-card-bg, rgba(255,255,255,0.06));
                    border-color: rgba(255,255,255,0.1);
                    color: var(--chat-text-color, #ddd);
                }
            }

            .option-button:hover:not(.selected) {
                border-color: var(--color-primary);
                background: rgba(var(--color-primary-rgb, 0,0,0), 0.05);
                transform: translateX(3px);
            }

            .option-button.selected {
                border-color: var(--color-primary);
                background: rgba(var(--color-primary-rgb, 0,0,0), 0.08);
                color: var(--color-primary);
                font-weight: 600;
            }

            .option-button.recommended {
                border-color: var(--color-positive, #0f7d15);
            }

            .option-button.recommended.selected {
                border-color: var(--color-positive, #0f7d15);
                background: rgba(15, 125, 21, 0.08);
                color: var(--color-positive, #0f7d15);
            }

            .radio-icon {
                font-size: 16px;
                flex-shrink: 0;
                color: inherit;
            }

            .option-label {
                flex: 1;
            }

            .badge-recommended {
                padding: 3px 8px;
                background: var(--color-positive, #0f7d15);
                color: white;
                border-radius: 8px;
                font-size: 10px;
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 0.4px;
                flex-shrink: 0;
            }

            .custom-input-container {
                margin-top: 10px;
                display: flex;
                gap: 8px;
            }

            .custom-input-container input {
                /* Sobrescreve .input-base do BookStack: height:40px, width:250px, display:inline-block */
                display: block !important;
                flex: 1 !important;
                width: 100% !important;
                height: auto !important;
                min-height: unset !important;
                padding: 11px 14px !important;
                border: 1.5px solid rgba(0, 0, 0, 0.1) !important;
                border-radius: 10px !important;
                font-size: 14px !important;
                background: var(--chat-card-bg, rgba(255,255,255,0.6)) !important;
                transition: all 0.15s;
                color: var(--chat-text-color, #333) !important;
                box-sizing: border-box !important;
            }

            @media (prefers-color-scheme: dark) {
                .custom-input-container input {
                    background: var(--chat-card-bg, rgba(255,255,255,0.06)) !important;
                    border-color: rgba(255,255,255,0.1) !important;
                    color: var(--chat-text-color, #ddd) !important;
                }
            }

            .custom-input-container input:focus {
                outline: none !important;
                border-color: var(--color-primary) !important;
            }

            .submit-custom-btn {
                padding: 0 14px;
                background: var(--chat-btn-color, var(--color-primary));
                color: white;
                border: none;
                border-radius: 10px;
                cursor: pointer;
                font-size: 16px;
                min-width: 44px;
                transition: opacity 0.15s;
            }

            .submit-custom-btn:disabled {
                opacity: 0.35;
                cursor: not-allowed;
            }

            /* Ícone de editar que substitui o radio no botão custom */
            .custom-edit-icon {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 24px;
                height: 24px;
                border-radius: 6px;
                background: rgba(0, 0, 0, 0.07);
                flex-shrink: 0;
                transition: background 0.15s;
                cursor: pointer;
                color: inherit;
            }

            .custom-edit-icon:hover {
                background: rgba(0, 0, 0, 0.14);
            }

            @media (prefers-color-scheme: dark) {
                .custom-edit-icon {
                    background: rgba(255, 255, 255, 0.10);
                }
                .custom-edit-icon:hover {
                    background: rgba(255, 255, 255, 0.18);
                }
            }

            .custom-option-btn.selected .custom-edit-icon {
                background: rgba(0, 0, 0, 0.10);
            }

            .custom-input-wrapper {
                margin-top: 8px;
            }

            .option-custom-inline {
                margin-top: 8px;
                animation: fadeInInlineInput 0.18s ease;
            }

            .option-custom-inline.hidden {
                display: none;
            }

            @keyframes fadeInInlineInput {
                from { opacity: 0; transform: translateY(-4px); }
                to   { opacity: 1; transform: translateY(0); }
            }

            .option-custom-inline-input {
                width: 100%;
                padding: 11px 14px;
                border: 1.5px solid var(--color-primary);
                border-radius: 10px;
                font-size: 14px;
                background: var(--chat-card-bg, rgba(255,255,255,0.6));
                color: var(--chat-text-color, #333);
                outline: none;
                transition: box-shadow 0.15s;
                box-shadow: 0 0 0 3px rgba(var(--color-primary-rgb, 0,0,200), 0.08);
            }

            .option-custom-inline-input:focus {
                box-shadow: 0 0 0 3px rgba(var(--color-primary-rgb, 0,0,200), 0.18);
            }

            @media (prefers-color-scheme: dark) {
                .option-custom-inline-input {
                    background: var(--chat-card-bg, rgba(255,255,255,0.06));
                    color: var(--chat-text-color, #ddd);
                }
            }
        `;
        document.head.appendChild(style);
    }

    /**
     * Fallback via CustomEvent DOM (mantido para outros contextos).
     */
    setupDirectListener() {
        document.addEventListener('ask-questions-show', (e: Event) => {
            const detail = (e as CustomEvent).detail;
            if (detail?.requestId && detail?.questions) {
                this.showQuestions({
                    requestId: detail.requestId,
                    questions: detail.questions,
                    context: detail.context || undefined,
                });
            }
        });
    }

    /**
     * Previne fechamento do modal sem resposta
     */
    private preventClosing() {
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && !this.overlay.classList.contains('hidden')) {
                e.preventDefault();
                this.showWarning();
            }
        });

        this.overlay.addEventListener('click', (e) => {
            if (e.target === this.overlay) {
                e.preventDefault();
                this.showWarning();
            }
        });
    }

    private showWarning() {
        alert('Este modal só pode ser fechado após responder todas as perguntas.');
    }

    /**
     * Exibe modal com perguntas
     */
    public showQuestions(payload: QuestionPayload) {
        this.currentPayload = payload;
        this.answers.clear();
        this.customInputs.clear();

        if (payload.context) {
            this.contextEl.textContent = `ℹ️  ${payload.context}`;
            this.contextEl.classList.remove('hidden');
        } else {
            this.contextEl.classList.add('hidden');
        }

        this.renderQuestions(payload.questions);
        this.overlay.classList.remove('hidden');

        // Ativa blur/block apenas no modo chat
        if (this.mode === 'chat') {
            const chatContainer = document.querySelector('.chat-box-container');
            if (chatContainer) chatContainer.classList.add('is-asking');
            document.querySelectorAll('.chat-sidebar, .chat-files-sidebar').forEach(el => {
                el.classList.add('is-asking-blocked');
            });
        }
    }

    private renderQuestions(questions: Question[]) {
        this.bodyEl.innerHTML = '';
        questions.forEach((question, index) => {
            const block = this.createQuestionBlock(question, index + 1, questions.length);
            this.bodyEl.appendChild(block);
        });
    }

    private createQuestionBlock(question: Question, index: number, total: number): HTMLElement {
        const block = document.createElement('div');
        block.className = 'question-block';

        const number = document.createElement('div');
        number.className = 'question-number';
        number.textContent = `Pergunta ${index} de ${total}`;
        block.appendChild(number);

        const text = document.createElement('div');
        text.className = 'question-text';
        text.textContent = question.question;
        block.appendChild(text);

        if (question.options && question.options.length > 0) {
            const optionsContainer = document.createElement('div');
            optionsContainer.className = 'options-container';
            // data-question-id no container: permite deselectOptions() funcionar corretamente
            optionsContainer.dataset.questionId = question.id;

            question.options.forEach(option => {
                const btn = this.createOptionButton(question.id, option);
                optionsContainer.appendChild(btn);
            });

            block.appendChild(optionsContainer);

            const hasCustomOption = question.options.some(o => o.allowCustom);
            if (hasCustomOption) {
                block.appendChild(this.createOptionCustomInput(question));
            }
        }

        if (question.allowCustom) {
            const customContainer = this.createCustomInput(question);
            block.appendChild(customContainer);
        }

        return block;
    }

    private createOptionCustomInput(question: Question): HTMLElement {
        const wrapper = document.createElement('div');
        wrapper.className = 'option-custom-inline hidden';
        wrapper.dataset.questionId = question.id;

        const input = document.createElement('input');
        input.type = 'text';
        input.maxLength = 150;
        input.placeholder = question.customPlaceholder || 'Especifique...';
        input.className = 'option-custom-inline-input';

        input.addEventListener('input', () => {
            const val = input.value.trim();
            if (val) {
                this.answers.set(question.id, val);
            } else {
                this.answers.delete(question.id);
            }
            this.checkAllAnswered();
        });

        wrapper.appendChild(input);
        return wrapper;
    }

    private createOptionButton(questionId: string, option: QuestionOption): HTMLElement {
        const btn = document.createElement('button');
        btn.className = 'option-button';
        btn.dataset.value = option.value;
        if (option.recommended) {
            btn.classList.add('recommended');
        }

        const radio = document.createElement('span');
        radio.className = 'radio-icon';
        radio.textContent = '○';

        const label = document.createElement('span');
        label.className = 'option-label';
        label.textContent = option.label;

        btn.appendChild(radio);
        btn.appendChild(label);

        if (option.recommended) {
            const badge = document.createElement('span');
            badge.className = 'badge-recommended';
            badge.textContent = 'Recomendado';
            btn.appendChild(badge);
        }

        if (option.allowCustom) {
            btn.dataset.allowCustom = 'true';
        }

        btn.addEventListener('click', () => this.selectOption(questionId, option.value, btn));

        return btn;
    }

    private createCustomInput(question: Question): HTMLElement {
        const wrapper = document.createElement('div');
        wrapper.className = 'custom-input-wrapper';

        // ── MODO INPUT ──
        const inputRow = document.createElement('div');
        inputRow.className = 'custom-input-container';
        inputRow.dataset.questionId = question.id;

        const input = document.createElement('input');
        input.type = 'text';
        input.maxLength = 150;
        input.placeholder = question.customPlaceholder || 'Outra resposta...';
        input.dataset.questionId = question.id;

        const submitBtn = document.createElement('button');
        submitBtn.className = 'submit-custom-btn';
        submitBtn.textContent = '✔';
        submitBtn.disabled = true;

        // Só armazena o valor — nunca auto-seleciona nem deseleciona as opções da IA
        input.addEventListener('input', () => {
            const val = input.value.trim();
            submitBtn.disabled = !val;
            if (val) {
                this.customInputs.set(question.id, val);
            } else {
                this.customInputs.delete(question.id);
            }
            // Questão sem opções da IA: atualiza resposta diretamente
            if (!question.options || question.options.length === 0) {
                if (val) {
                    this.answers.set(question.id, val);
                } else {
                    this.answers.delete(question.id);
                }
                this.checkAllAnswered();
            }
        });

        // ── MODO OPÇÃO ──
        const optionBtn = document.createElement('button');
        optionBtn.className = 'option-button custom-option-btn hidden';
        optionBtn.dataset.questionId = question.id;
        optionBtn.dataset.value = '';

        const editIcon = document.createElement('span');
        editIcon.className = 'custom-edit-icon';
        editIcon.title = 'Editar';
        editIcon.innerHTML = '<svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><path d="M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 0 0-.708zm.646 6.061L9.793 2.5 3.293 9H3.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.207zm-7.5 7.5-.707-.707 7-7 .707.707zM1 13.5V15h1.5l4.407-4.407-1.5-1.5z"/></svg>';

        const optionLabel = document.createElement('span');
        optionLabel.className = 'option-label';

        optionBtn.appendChild(editIcon);
        optionBtn.appendChild(optionLabel);

        // Clique no botão opção → seleciona como resposta (igual às opções da IA)
        optionBtn.addEventListener('click', (e) => {
            if ((e.target as HTMLElement).closest('.custom-edit-icon')) return;
            const value = this.customInputs.get(question.id) || '';
            if (!value) return;
            this.selectOption(question.id, value, optionBtn);
        });

        // Clique no ícone de editar → volta ao modo input
        editIcon.addEventListener('click', (e) => {
            e.stopPropagation();
            // Desmarca se estava selecionada
            if (optionBtn.classList.contains('selected')) {
                optionBtn.classList.remove('selected');
                this.answers.delete(question.id);
                this.checkAllAnswered();
            }
            // Volta ao modo input
            optionBtn.classList.add('hidden');
            inputRow.classList.remove('hidden');
            input.value = this.customInputs.get(question.id) || '';
            submitBtn.disabled = !input.value.trim();
            input.focus();
        });

        // Confirmar: converte para botão-opção e já o seleciona automaticamente
        const confirmCustom = () => {
            const value = input.value.trim();
            if (!value) return;
            this.customInputs.set(question.id, value);
            optionBtn.dataset.value = value;
            optionLabel.textContent = value;
            inputRow.classList.add('hidden');
            optionBtn.classList.remove('hidden');
            // Auto-seleciona após confirmar o texto customizado
            this.selectOption(question.id, value, optionBtn);
        };

        input.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Enter') confirmCustom();
        });
        submitBtn.addEventListener('click', confirmCustom);

        inputRow.appendChild(input);
        inputRow.appendChild(submitBtn);
        wrapper.appendChild(inputRow);
        wrapper.appendChild(optionBtn);

        return wrapper;
    }

    private selectOption(questionId: string, value: string, btn: HTMLElement) {
        this.deselectOptions(questionId);

        btn.classList.add('selected');
        const radio = btn.querySelector('.radio-icon');
        if (radio) radio.textContent = '●';

        const inlineWrapper = this.bodyEl.querySelector<HTMLElement>(`.option-custom-inline[data-question-id="${questionId}"]`);
        if (inlineWrapper) {
            if (btn.dataset.allowCustom === 'true') {
                inlineWrapper.classList.remove('hidden');
                const inlineInput = inlineWrapper.querySelector<HTMLInputElement>('input');
                if (inlineInput) {
                    inlineInput.focus();
                    const existing = inlineInput.value.trim();
                    if (existing) {
                        this.answers.set(questionId, existing);
                    } else {
                        this.answers.delete(questionId);
                    }
                    this.checkAllAnswered();
                    return;
                }
            }
        }

        const inputRow = this.bodyEl.querySelector<HTMLElement>(`.custom-input-container[data-question-id="${questionId}"]`);
        if (inputRow && !inputRow.classList.contains('hidden')) {
            const input = inputRow.querySelector('input') as HTMLInputElement;
            if (input) {
                input.value = '';
                input.style.borderColor = '';
            }
            this.customInputs.delete(questionId);
            // Desativa botão ✔
            const submitBtn = inputRow.querySelector('.submit-custom-btn') as HTMLButtonElement;
            if (submitBtn) submitBtn.disabled = true;
        }

        this.answers.set(questionId, value);
        this.checkAllAnswered();
    }

    /**
     * Desmarca todas as opÃ§Ãµes de uma pergunta.
     * Usa data-question-id no optionsContainer para encontrar o grupo correto.
     */
    private deselectOptions(questionId: string) {
        const container = this.bodyEl.querySelector<HTMLElement>(`.options-container[data-question-id="${questionId}"]`);
        if (container) {
            container.querySelectorAll<HTMLElement>('.option-button').forEach(btn => {
                btn.classList.remove('selected');
                const radio = btn.querySelector('.radio-icon');
                if (radio) radio.textContent = '○';
            });
        }
        const customBtn = this.bodyEl.querySelector<HTMLElement>(`.custom-option-btn[data-question-id="${questionId}"]`);
        if (customBtn) customBtn.classList.remove('selected');

        const inlineWrapper = this.bodyEl.querySelector<HTMLElement>(`.option-custom-inline[data-question-id="${questionId}"]`);
        if (inlineWrapper) {
            inlineWrapper.classList.add('hidden');
        }
    }

    private checkAllAnswered() {
        if (!this.currentPayload) return;
        const allAnswered = this.currentPayload.questions.every(q => this.answers.has(q.id));
        this.confirmBtn.disabled = !allAnswered;
    }

    private async confirmAnswers() {
        if (!this.currentPayload) return;
        this.checkAllAnswered();
        if (this.confirmBtn.disabled) return;

        const answersArray: Answer[] = Array.from(this.answers.entries()).map(([questionId, answer]) => ({
            questionId,
            answer,
            isCustom: this.customInputs.has(questionId),
        }));

        try {
            this.confirmBtn.disabled = true;
            this.confirmBtn.textContent = 'Enviando...';

            const response = await window.$http.post('/api/ai/ask-questions/respond', {
                request_id: this.currentPayload.requestId,
                answers: answersArray,
            });

            const responseData = response.data as { success: boolean; message?: string };

            if (responseData.success) {
                this.overlay.classList.add('hidden');
                // Reseta botão para próxima vez que o modal abrir
                this.confirmBtn.textContent = 'Confirmar Respostas';
                this.confirmBtn.disabled = true;
                this.currentPayload = null;
                this.answers.clear();
                this.customInputs.clear();
                // Remove blur e escudo apenas no modo chat
                if (this.mode === 'chat') {
                    const chatContainer = document.querySelector('.chat-box-container');
                    if (chatContainer) chatContainer.classList.remove('is-asking');
                    document.querySelectorAll('.chat-sidebar, .chat-files-sidebar').forEach(el => {
                        el.classList.remove('is-asking-blocked');
                    });
                }
            } else {
                throw new Error(responseData.message || 'Erro ao enviar respostas');
            }
        } catch (error) {
            aiLogger.error('Erro ao enviar respostas:', error);
            if (window.$events) {
                window.$events.error('Erro ao enviar respostas. Tente novamente.');
            }
            this.confirmBtn.disabled = false;
            this.confirmBtn.textContent = 'Confirmar Respostas';
        }
    }

    /**
     * Remove blur e escudo (chamado externamente se necessário — ex: timeout).
     */
    public dismiss() {
        this.overlay.classList.add('hidden');
        if (this.mode === 'chat') {
            const chatContainer = document.querySelector('.chat-box-container');
            if (chatContainer) chatContainer.classList.remove('is-asking');
            document.querySelectorAll('.chat-sidebar, .chat-files-sidebar').forEach(el => {
                el.classList.remove('is-asking-blocked');
            });
        }
        this.currentPayload = null;
        this.answers.clear();
        this.customInputs.clear();
    }
}
